import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Mail, Smartphone, Volume2, VolumeX, Calendar,
  ChevronLeft, Save, Loader2, Check, Clock, Package,
  Truck, Megaphone, AlertCircle, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`,
}));

export default function NotificationSettingsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    // Push notification preferences
    push_enabled: true,
    push_orders: true,
    push_promotions: true,
    push_delivery: true,
    push_system: true,
    
    // Email preferences
    email_enabled: true,
    email_orders: true,
    email_promotions: false,
    email_weekly_digest: false,
    email_daily_digest: false,
    
    // In-app preferences
    in_app_enabled: true,
    in_app_sound: true,
    
    // Digest preferences
    digest_frequency: 'none',
    digest_day: 1,
    digest_hour: 9,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/notification-preferences');
      if (response.data?.preferences) {
        setPreferences(prev => ({ ...prev, ...response.data.preferences }));
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      await api.put('/users/notification-preferences', preferences);
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Notification Settings</h1>
                <p className="text-sm text-slate-500">Manage how you receive notifications</p>
              </div>
            </div>
            <Button
              onClick={savePreferences}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Push Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle>Push Notifications</CardTitle>
                <CardDescription>Notifications sent to your mobile device</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label className="font-medium">Enable Push Notifications</Label>
                <p className="text-sm text-slate-500">Receive notifications on your device</p>
              </div>
              <Switch
                checked={preferences.push_enabled}
                onCheckedChange={(checked) => updatePreference('push_enabled', checked)}
              />
            </div>
            
            {preferences.push_enabled && (
              <div className="border-t pt-4 space-y-4 ml-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-slate-500" />
                    <Label>Order Updates</Label>
                  </div>
                  <Switch
                    checked={preferences.push_orders}
                    onCheckedChange={(checked) => updatePreference('push_orders', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Truck className="w-4 h-4 text-slate-500" />
                    <Label>Delivery Updates</Label>
                  </div>
                  <Switch
                    checked={preferences.push_delivery}
                    onCheckedChange={(checked) => updatePreference('push_delivery', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Megaphone className="w-4 h-4 text-slate-500" />
                    <Label>Promotions & Offers</Label>
                  </div>
                  <Switch
                    checked={preferences.push_promotions}
                    onCheckedChange={(checked) => updatePreference('push_promotions', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-4 h-4 text-slate-500" />
                    <Label>System Notifications</Label>
                  </div>
                  <Switch
                    checked={preferences.push_system}
                    onCheckedChange={(checked) => updatePreference('push_system', checked)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>Notifications sent to your email</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label className="font-medium">Enable Email Notifications</Label>
                <p className="text-sm text-slate-500">Receive notifications via email</p>
              </div>
              <Switch
                checked={preferences.email_enabled}
                onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
              />
            </div>
            
            {preferences.email_enabled && (
              <div className="border-t pt-4 space-y-4 ml-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-slate-500" />
                    <Label>Order Confirmations</Label>
                  </div>
                  <Switch
                    checked={preferences.email_orders}
                    onCheckedChange={(checked) => updatePreference('email_orders', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Megaphone className="w-4 h-4 text-slate-500" />
                    <Label>Marketing Emails</Label>
                  </div>
                  <Switch
                    checked={preferences.email_promotions}
                    onCheckedChange={(checked) => updatePreference('email_promotions', checked)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Digest */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Email Digest</CardTitle>
                <CardDescription>Receive a summary of your activity</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="font-medium">Digest Frequency</Label>
              <div className="flex gap-2">
                {['none', 'daily', 'weekly'].map((freq) => (
                  <Button
                    key={freq}
                    variant={preferences.digest_frequency === freq ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updatePreference('digest_frequency', freq)}
                    className={preferences.digest_frequency === freq ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    {freq === 'none' ? 'Off' : freq.charAt(0).toUpperCase() + freq.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            
            {preferences.digest_frequency !== 'none' && (
              <div className="border-t pt-4 space-y-4">
                {preferences.digest_frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <select
                      value={preferences.digest_day}
                      onChange={(e) => updatePreference('digest_day', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {DAYS_OF_WEEK.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Time to Send</Label>
                  <select
                    value={preferences.digest_hour}
                    onChange={(e) => updatePreference('digest_hour', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {HOURS.map((hour) => (
                      <option key={hour.value} value={hour.value}>
                        {hour.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* In-App Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <CardTitle>In-App Notifications</CardTitle>
                <CardDescription>Notifications shown within the app</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label className="font-medium">Enable In-App Notifications</Label>
                <p className="text-sm text-slate-500">Show notifications in the app</p>
              </div>
              <Switch
                checked={preferences.in_app_enabled}
                onCheckedChange={(checked) => updatePreference('in_app_enabled', checked)}
              />
            </div>
            
            {preferences.in_app_enabled && (
              <div className="border-t pt-4 space-y-4 ml-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {preferences.in_app_sound ? (
                      <Volume2 className="w-4 h-4 text-slate-500" />
                    ) : (
                      <VolumeX className="w-4 h-4 text-slate-500" />
                    )}
                    <Label>Sound</Label>
                  </div>
                  <Switch
                    checked={preferences.in_app_sound}
                    onCheckedChange={(checked) => updatePreference('in_app_sound', checked)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
